import { api } from "@slapinc/slappos-backend/api";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery } from "convex/react";
import type { GenericId as Id } from "convex/values";
import { useCallback, useState } from "react";
import { Can } from "@/components/can";
import { PageContent } from "@/components/layout/page-content";
import { ChangeRoleModal } from "@/components/modals/change-role-modal";
import { InviteMemberModal } from "@/components/modals/invite-member-modal";
import { PermissionGuard } from "@/components/permission-guard";
import { ActionHeaderButton } from "@/components/ui/action-header-button";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { StepUpTotpDialog } from "@/components/ui/step-up-totp-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MembersTable } from "@/features/members/_components/members-table";
import { PendingInvitesTable } from "@/features/members/_components/pending-invites-table";
import { usePagination } from "@/hooks/use-pagination";
import { useRequireStepUp } from "@/hooks/use-require-step-up";
import { handleConvexError } from "@/lib/errors";
import type { InviteFormInput } from "@/lib/schemas/invite";
import { toast } from "@/lib/toast";
import type { Member, PendingInvite, Role } from "@/lib/types/member";

export const Route = createFileRoute("/(dashboard)/settings/members")({
	component: () => (
		<PermissionGuard scope="members.read">
			<MembersPage />
		</PermissionGuard>
	),
});

const PAGE_SIZE = 10;

function MembersPage() {
	// Pagination
	const { pagination, onPaginationChange } = usePagination(PAGE_SIZE);
	const {
		pagination: invitesPagination,
		onPaginationChange: onInvitesPaginationChange,
	} = usePagination(PAGE_SIZE);

	// Modal state
	const [inviteModalOpen, setInviteModalOpen] = useState(false);
	const [changeRoleModalOpen, setChangeRoleModalOpen] = useState(false);
	const [selectedMember, setSelectedMember] = useState<Member | null>(null);
	const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
	const [memberToRemove, setMemberToRemove] = useState<Member | null>(null);
	const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
	const [inviteToRevoke, setInviteToRevoke] = useState<PendingInvite | null>(
		null,
	);

	// Loading states
	const [isInviting, setIsInviting] = useState(false);
	const [isChangingRole, setIsChangingRole] = useState(false);
	const [isRemoving, setIsRemoving] = useState(false);
	const [isRevoking, setIsRevoking] = useState(false);

	// Queries
	const membersData = useQuery(api.features.members.web.list, {});
	const invitesData = useQuery(api.features.invites.web.listPending, {});
	const rolesData = useQuery(api.features.roles.web.list, {});

	const members = (membersData ?? []) as Member[];
	const pendingInvites = (invitesData ?? []) as PendingInvite[];
	const roles = (rolesData ?? []) as Role[];
	const isLoading =
		membersData === undefined ||
		invitesData === undefined ||
		rolesData === undefined;

	// Mutations
	const createInvite = useMutation(api.features.invites.web.create);
	const resendInvite = useMutation(api.features.invites.web.resend);
	const revokeInvite = useMutation(api.features.invites.web.revoke);
	const updateMemberRole = useMutation(api.features.members.web.updateRole);
	const removeMember = useMutation(api.features.members.web.remove);
	const stepUp = useRequireStepUp();

	// Paginate members (client-side)
	const paginatedMembers = members.slice(
		pagination.pageIndex * pagination.pageSize,
		(pagination.pageIndex + 1) * pagination.pageSize,
	);

	// Paginate invites (client-side)
	const paginatedInvites = pendingInvites.slice(
		invitesPagination.pageIndex * invitesPagination.pageSize,
		(invitesPagination.pageIndex + 1) * invitesPagination.pageSize,
	);

	// Handlers
	const handleInvite = useCallback(
		async (data: InviteFormInput) => {
			setIsInviting(true);
			try {
				for (const invite of data.invites) {
					await createInvite({
						email: invite.email,
						roleId: invite.roleId as Id<"organizationRoles">,
					});
				}
				toast.success(
					data.invites.length > 1 ? (
						<>{data.invites.length} invites sent</>
					) : (
						<>Invite sent</>
					),
				);
			} catch (error) {
				handleConvexError(error, "Failed to send invite");
				throw error;
			} finally {
				setIsInviting(false);
			}
		},
		[createInvite],
	);

	const handleChangeRole = useCallback((member: Member) => {
		setSelectedMember(member);
		setChangeRoleModalOpen(true);
	}, []);

	const handleUpdateRole = useCallback(
		async (memberId: string, roleId: string) => {
			setIsChangingRole(true);
			try {
				await stepUp.runWithStepUp({
					intent: "member:change-role",
					title: "Change role?",
					description:
						"Enter your authenticator code to change this member's role.",
					action: async (stepUpToken) => {
						await updateMemberRole({
							memberId: memberId as Id<"organizationMembers">,
							roleId: roleId as Id<"organizationRoles">,
							stepUpToken,
						});
						toast.success(<>Role updated</>);
					},
				});
			} catch (error) {
				handleConvexError(error, "Failed to update role");
				throw error;
			} finally {
				setIsChangingRole(false);
			}
		},
		[updateMemberRole, stepUp],
	);

	const handleRemove = useCallback((member: Member) => {
		setMemberToRemove(member);
		setRemoveDialogOpen(true);
	}, []);

	const confirmRemove = useCallback(async () => {
		if (!memberToRemove) return;

		setIsRemoving(true);
		try {
			const target = memberToRemove;
			await stepUp.runWithStepUp({
				intent: "member:remove",
				title: "Remove member?",
				description:
					"Enter your authenticator code to remove this member from the organization.",
				action: async (stepUpToken) => {
					await removeMember({ memberId: target._id, stepUpToken });
					toast.success(<>Member removed</>);
					setRemoveDialogOpen(false);
					setMemberToRemove(null);
				},
			});
		} catch (error) {
			handleConvexError(error, "Failed to remove member");
		} finally {
			setIsRemoving(false);
		}
	}, [memberToRemove, removeMember, stepUp]);

	const handleResendInvite = useCallback(
		async (invite: PendingInvite) => {
			try {
				await resendInvite({ inviteId: invite._id });
				toast.success(<>Invite resent</>);
			} catch (error) {
				handleConvexError(error, "Failed to resend invite");
			}
		},
		[resendInvite],
	);

	const handleRevokeInvite = useCallback((invite: PendingInvite) => {
		setInviteToRevoke(invite);
		setRevokeDialogOpen(true);
	}, []);

	const confirmRevoke = useCallback(async () => {
		if (!inviteToRevoke) return;

		setIsRevoking(true);
		try {
			await revokeInvite({ inviteId: inviteToRevoke._id });
			toast.success(<>Invite revoked</>);
			setRevokeDialogOpen(false);
			setInviteToRevoke(null);
		} catch (error) {
			handleConvexError(error, "Failed to revoke invite");
		} finally {
			setIsRevoking(false);
		}
	}, [inviteToRevoke, revokeInvite]);

	return (
		<PageContent
			title="Members"
			controls={
				<Can scope="invites.create">
					<ActionHeaderButton
						label="Invite"
						onClick={() => setInviteModalOpen(true)}
					/>
				</Can>
			}
		>
			<Tabs defaultValue="members">
				<TabsList>
					<TabsTrigger value="members">
						Members{members.length > 0 && ` (${members.length})`}
					</TabsTrigger>
					<TabsTrigger value="invites">
						Pending{pendingInvites.length > 0 && ` (${pendingInvites.length})`}
					</TabsTrigger>
				</TabsList>

				<TabsContent value="members">
					<MembersTable
						data={paginatedMembers}
						loading={isLoading}
						pagination={pagination}
						onPaginationChange={onPaginationChange}
						rowCount={members.length}
						onChangeRole={handleChangeRole}
						onRemove={handleRemove}
					/>
				</TabsContent>

				<TabsContent value="invites">
					<PendingInvitesTable
						data={paginatedInvites}
						roles={roles}
						loading={isLoading}
						pagination={invitesPagination}
						onPaginationChange={onInvitesPaginationChange}
						rowCount={pendingInvites.length}
						onResend={handleResendInvite}
						onRevoke={handleRevokeInvite}
					/>
				</TabsContent>
			</Tabs>

			{/* Invite Modal */}
			<InviteMemberModal
				open={inviteModalOpen}
				onOpenChange={setInviteModalOpen}
				roles={roles}
				onSubmit={handleInvite}
				isSubmitting={isInviting}
			/>

			{/* Change Role Modal */}
			<ChangeRoleModal
				open={changeRoleModalOpen}
				onOpenChange={setChangeRoleModalOpen}
				member={selectedMember}
				roles={roles}
				onSubmit={handleUpdateRole}
				isSubmitting={isChangingRole}
			/>

			{/* Remove Member Confirmation */}
			<AlertDialog open={removeDialogOpen} onOpenChange={setRemoveDialogOpen}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Remove member?</AlertDialogTitle>
						<AlertDialogDescription>
							Are you sure you want to remove "
							{memberToRemove?.user?.name ||
								memberToRemove?.user?.email ||
								"this member"}
							" from the organization? They will lose access immediately.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={isRemoving}>Cancel</AlertDialogCancel>
						<AlertDialogAction
							variant="destructive"
							onClick={confirmRemove}
							disabled={isRemoving}
						>
							{isRemoving ? "Removing..." : "Remove"}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			{/* Revoke Invite Confirmation */}
			<AlertDialog open={revokeDialogOpen} onOpenChange={setRevokeDialogOpen}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Revoke invite?</AlertDialogTitle>
						<AlertDialogDescription>
							Are you sure you want to revoke the invitation for "
							{inviteToRevoke?.email}"? They will no longer be able to join with
							this invite link.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={isRevoking}>Cancel</AlertDialogCancel>
						<AlertDialogAction
							variant="destructive"
							onClick={confirmRevoke}
							disabled={isRevoking}
						>
							{isRevoking ? "Revoking..." : "Revoke"}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			<StepUpTotpDialog {...stepUp.dialogProps} />
		</PageContent>
	);
}
